import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Waves, Loader2, User, Briefcase } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState(searchParams.get("ref") || "");
  const [accountType, setAccountType] = useState("particulier");
  const [companyName, setCompanyName] = useState("");
  const [siret, setSiret] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Mot de passe : minimum 6 caractères");
      return;
    }
    setLoading(true);
    try {
      await register(
        email,
        password,
        name,
        referralCode.trim().toUpperCase() || undefined,
        {
          account_type: accountType,
          company_name: accountType === "pro" ? (companyName.trim() || undefined) : undefined,
          siret: accountType === "pro" ? (siret.trim() || undefined) : undefined,
        }
      );
      toast.success(
        accountType === "pro"
          ? "Compte Pro créé — bienvenue !"
          : referralCode
          ? "Compte créé — bienvenue via parrainage !"
          : "Compte créé"
      );
      navigate("/diagnostic", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative bg-secondary overflow-hidden">
        <img
          src="https://customer-assets-v7afamib.emergentagent.net/job_spa-report-hub/artifacts/i4l1k62f_542DB015-7DA7-4F8F-A41A-8344510A2CB6_1_105_c.jpeg"
          alt="Spa jacuzzi extérieur"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/85 via-slate-950/70 to-slate-950/60" />
        <div className="absolute inset-0 grid-pattern-dense opacity-10" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2">
            <span className="grid place-items-center h-9 w-9 rounded-md bg-white/10 backdrop-blur-sm">
              <Waves className="h-5 w-5" />
            </span>
            <span className="font-display font-black text-lg">DoctoSpa</span>
          </Link>
          <div>
            <div className="eyebrow text-white/60">Créer un compte</div>
            <h2 className="mt-3 font-display text-4xl font-black tracking-tighter">
              Diagnostic gratuit,<br />rapports illimités.
            </h2>
            <p className="mt-4 text-white/70 text-sm max-w-md">
              Particuliers et professionnels : créez votre compte en 30 secondes
              et lancez votre premier diagnostic.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <Card className="w-full max-w-md p-8 border-black/10 shadow-sm">
          <div className="eyebrow">Inscription</div>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">Créer votre compte</h1>
          <p className="mt-2 text-sm text-muted-foreground">Gratuit, sans carte bancaire.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            {/* Account type radio */}
            <div className="space-y-2">
              <Label>Vous êtes</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setAccountType("particulier")}
                  data-testid="account-type-particulier"
                  className={`text-left rounded-xl border px-4 py-3 transition-colors ${
                    accountType === "particulier"
                      ? "border-primary bg-primary/5"
                      : "border-black/10 hover:border-primary/40"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">Particulier</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Propriétaire d'un spa domestique
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setAccountType("pro")}
                  data-testid="account-type-pro"
                  className={`text-left rounded-xl border px-4 py-3 transition-colors ${
                    accountType === "pro"
                      ? "border-primary bg-primary/5"
                      : "border-black/10 hover:border-primary/40"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">Professionnel</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Technicien, installateur, SAV
                  </p>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">
                {accountType === "pro" ? "Contact (Nom & Prénom)" : "Prénom / Nom"}
              </Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)}
                data-testid="register-name" placeholder="Jean Dupont" />
            </div>

            {/* Pro-only optional fields */}
            {accountType === "pro" && (
              <div className="space-y-4 rounded-xl border border-black/10 bg-muted/40 p-4">
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                  Informations société <span className="opacity-60 font-normal normal-case">— optionnel</span>
                </p>
                <div className="space-y-2">
                  <Label htmlFor="company_name">Raison sociale</Label>
                  <Input id="company_name" value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    data-testid="register-company-name" placeholder="SpaTech SARL" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="siret">SIRET</Label>
                  <Input id="siret" value={siret}
                    onChange={(e) => setSiret(e.target.value.replace(/\s+/g, ""))}
                    data-testid="register-siret" placeholder="12345678901234"
                    inputMode="numeric" maxLength={14} className="font-mono" />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} data-testid="register-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" required minLength={6} value={password}
                onChange={(e) => setPassword(e.target.value)} data-testid="register-password" />
              <p className="text-xs text-muted-foreground">6 caractères minimum.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="referral" className="flex items-center gap-2">
                Code de parrainage
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-normal">Optionnel</span>
              </Label>
              <Input id="referral" value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                data-testid="register-referral" placeholder="ex : ABCD1234"
                className="uppercase tracking-widest font-mono" />
              {referralCode && (
                <p className="text-xs text-emerald-700 font-medium">
                  ✓ Votre parrain recevra 30 jours Premium offerts.
                </p>
              )}
            </div>

            <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="register-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer mon compte"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-center text-muted-foreground">
            Déjà inscrit ?{" "}
            <Link to="/login" className="font-semibold text-primary hover:underline" data-testid="link-to-login">
              Se connecter
            </Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
